/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback, useEffect } from 'react'
import { useIntersectionObserver } from 'usehooks-ts'
import { cn } from '~/lib/utils'
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem
} from '~/components/UI/shadcn/carousel'
import Fade from 'embla-carousel-fade'

import BlockVideo from '~/components/Media/components/BlockVideo'
import Image from '~/components/Helpers/Image'
import Badge from '~/components/UI/General/Badge/Badge'

const MediaSlide = ({
  slide,
  inView,
  reverse,
  contained,
  btnClasses
}: MediaSlideProps) => {
  const badge = slide.badge
  const [, setHasLoaded] = useState(false)

  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.5
  })

  const setLoaded = useCallback(() => {
    if (inView) setHasLoaded(true)
  }, [inView, setHasLoaded])

  return (
    <CarouselItem ref={ref}>
      <div
        className={cn(
          reverse ? 'justify-start' : 'justify-end',
          contained
            ? 'aspect-4/3 h-full'
            : 'aspect-16/9 items-center xl:aspect-auto xl:max-h-[680px]',
          'relative flex min-h-[300px] w-full sm:min-h-auto sm:basis-2/3'
        )}
      >
        {slide.video && slide.image ? (
          <BlockVideo
            videoUrl={slide?.video?.sys.uri}
            inView={inView}
            width={''}
            height={''}
            image={slide.image}
            onLoad={setLoaded}
            pause={!isIntersecting}
            className={contained ? '' : 'xl:min-h-[680px]'}
            btnPosition={reverse ? 'bottom-right' : 'bottom-left'}
            btnClassName={cn(
              btnClasses
              // '!bottom-m sm:!bottom-xl lg:!bottom-s'
            )}
          />
        ) : (
          slide.image && (
            <Image
              image={slide.image}
              classes={contained ? '' : 'xl:min-h-[680px]'}
            />
          )
        )}
      </div>
      {badge && badge.length > 0 && (
        <Badge
          className={cn(
            'sm:top-l ml-xs-plus sm:left-l absolute top-0 -translate-y-1/2 sm:translate-0 lg:ml-0',
            reverse ? 'lg:right-l lg:left-auto' : ''
          )}
          data={badge[0]}
        />
      )}
    </CarouselItem>
  )
}

const MediaCarousel = ({
  slides,
  current,
  setCurrent,
  contained,
  reverse,
  className,
  btnClasses
}: MediaCarouselProps) => {
  const [api, setApi] = useState<CarouselApi>()
  const [slidesInView, setSlidesInView] = useState<number[]>([])

  const updateSlidesInView = useCallback((api: CarouselApi) => {
    if (!api) return

    setSlidesInView(slidesInView => {
      if (slidesInView.length === api.slideNodes().length) {
        api.off('slidesInView', updateSlidesInView)
      }
      const inView = api
        .slidesInView()
        .filter((index: number) => !slidesInView.includes(index))
      return slidesInView.concat(inView)
    })
  }, [])

  useEffect(() => {
    if (!api) return

    updateSlidesInView(api)

    api.on('slidesInView', updateSlidesInView)
    api.on('reInit', updateSlidesInView)
  }, [api, updateSlidesInView])

  useEffect(() => {
    if (!api) {
      return
    }

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1)
    })
  }, [api])

  useEffect(() => {
    api?.scrollTo(current - 1)
  }, [current])

  return (
    <Carousel setApi={setApi} plugins={[Fade()]} className={className}>
      <CarouselContent>
        {slides?.map((slide: any, index: Index) => (
          <MediaSlide
            slide={slide}
            key={`content-${index}`}
            inView={slidesInView.indexOf(index) > -1}
            contained={contained}
            reverse={reverse}
            btnClasses={btnClasses}
          />
        ))}
      </CarouselContent>
    </Carousel>
  )
}

export default MediaCarousel
